import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FolderIcon from '@mui/icons-material/Folder';
import PreviewIcon from '@mui/icons-material/Preview';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningIcon from '@mui/icons-material/Warning';
import { Breadcrumbs, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { getLinkedinProjectById } from 'API/linkedinproject';
import PaiLink from 'features/ui/base-components/pai-link';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import { AnalysisStatus, LinkedinProject, SuspensionReasons } from './profile-generator.types';

interface Props {
  project: LinkedinProject;
  isEven: boolean;
  highlight: boolean;

  startAnalysis(project: LinkedinProject): () => void;

  handleRestartAnalysis(project: LinkedinProject): () => void;

  downloadOverallAnalysisResults(id: string): () => void;

  downloadPersonaListAnalysisResults(id: string): () => void;

  handleOpenResultsPreview(projectId: string): () => void;
}

const LinkedinProjectItem: React.FC<Props> = ({
  project,
  isEven,
  highlight,
  startAnalysis,
  handleRestartAnalysis,
  downloadOverallAnalysisResults,
  downloadPersonaListAnalysisResults,
  handleOpenResultsPreview,
}) => {
  const { classes } = useStyles({ isEven });
  const { t } = useTranslation();
  useUserData();

  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>(AnalysisStatus.NOT_CONNECTED);
  const [analyzedContacts, setAnalyzedContacts] = useState<number>();

  let projectInterval: any;

  const fetchSingleProject = async () => {
    const res = await getLinkedinProjectById(project.id);

    if (res && !res.error) {
      const resProject: LinkedinProject = res;

      setAnalyzedContacts(resProject.linkedinContacts.filter((contact) => contact.isAnalyzed).length);

      if (!_.isNil(projectInterval) && resProject.analysisFinished) {
        clearInterval(projectInterval);
        setAnalysisStatus(AnalysisStatus.DONE);
      }
    }
  };

  useEffect(() => {
    if (!project.status) {
      setAnalysisStatus(AnalysisStatus.EXPIRED);
    } else if (!project.analysisStarted) {
      setAnalysisStatus(AnalysisStatus.NOT_CONNECTED);
    } else if (project.isSuspended) {
      setAnalysisStatus(AnalysisStatus.SUSPENDED);
    } else if (project.analysisStarted && !project.analysisFinished) {
      setAnalysisStatus(AnalysisStatus.PENDING);
    } else if (project.analysisFinished) {
      setAnalysisStatus(AnalysisStatus.DONE);
    }

    if (project.analysisStarted && !project.analysisFinished) {
      projectInterval = setInterval(() => {
        fetchSingleProject();
      }, 1 * 60 * 1000);
    }

    return () => {
      clearInterval(projectInterval);
    };
  }, [project.analysisStarted, project.analysisFinished, project.isSuspended, project.status]);

  const countAnalyzedContacts = () => {
    return project.linkedinContacts.filter((contact) => contact.isAnalyzed).length;
  };

  const generateProgressCount = () => {
    const currentAnalyzed = _.isNil(analyzedContacts) ? countAnalyzedContacts() : analyzedContacts;
    const totalContacts = project.linkedinContacts.length;

    return (
      <>
        <PaiDotLoader size={'small'} inline={true} />
        <Typography className={classes.statusText} marginLeft={'16px'}>
          {t('progressCount', { current: currentAnalyzed, total: totalContacts })}
        </Typography>
      </>
    );
  };

  const generateSuspensionReason = () => {
    switch (project.suspensionReason) {
      case SuspensionReasons.COOKIES_INVALID:
        return `${t('reason')}: ${t('cookieInvalidTitle')}`;

      default:
        if (!_.isNil(project.suspensionReason)) {
          return `${t('reason')}: ${project.suspensionReason}`;
        }
        return `${t('reason')}: ${t('unknownError')}`;
    }
  };

  /**
   * Download handlers
   */

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const downloadOverallFile = () => {
    downloadOverallAnalysisResults(project.id)();
    handleCloseMenu();
  };

  const downloadZipFolder = () => {
    downloadPersonaListAnalysisResults(project.id)();
    handleCloseMenu();
  };

  return (
    <>
      <div className={classes.crawlerProject}>
        <Stack direction={'row'} gap={1} style={{ width: '100%' }} alignItems={'center'} justifyContent={'space-between'} flexWrap={'wrap'}>
          <Typography className={classes.projectName}>{`${project.name} - ${project.linkedinContacts.length} ${
            project.linkedinContacts.length > 1 ? t('profiles') : t('profile')
          }`}</Typography>
          <Breadcrumbs className={classes.breadcrumbs}>
            {analysisStatus === AnalysisStatus.DONE ? (
              <div className={classes.flexContainer}>
                <FileDownloadIcon className={classes.icon} />
                <PaiLink text={t('export')} size={'sm'} variant={'primary'} underline={'always'} textTransform={'lowercase'} action={openMenu} />
              </div>
            ) : null}
            {analysisStatus === AnalysisStatus.SUSPENDED ? (
              <div className={classes.flexContainer}>
                <RestartAltIcon className={classes.icon} />
                <PaiLink
                  text={t('restart')}
                  size={'sm'}
                  variant={'primary'}
                  underline={'always'}
                  textTransform={'lowercase'}
                  action={handleRestartAnalysis(project)}
                />
              </div>
            ) : null}
            <div className={classes.flexContainer}>
              {analysisStatus === AnalysisStatus.DONE ? (
                <>
                  <CheckCircleIcon className={classes.icon} />
                  <Typography className={classes.statusText}>{t('completed')}</Typography>
                </>
              ) : analysisStatus === AnalysisStatus.NOT_CONNECTED ? (
                <PaiLink
                  text={t('startNow')}
                  size={'sm'}
                  variant={'primary'}
                  underline={'always'}
                  textTransform={highlight ? 'uppercase' : 'lowercase'}
                  action={startAnalysis(project)}
                />
              ) : analysisStatus === AnalysisStatus.SUSPENDED ? (
                <>
                  <Tooltip title={generateSuspensionReason()}>
                    <WarningIcon className={classes.warnIcon} />
                  </Tooltip>
                  <Typography className={classes.statusText}>{t('suspended')}</Typography>
                </>
              ) : null}
            </div>
          </Breadcrumbs>
          {analysisStatus === AnalysisStatus.PENDING ? <div className={classes.flexContainer}>{generateProgressCount()}</div> : null}
        </Stack>
      </div>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MenuItem className={classes.menuItem} onClick={downloadOverallFile}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText className={classes.menuText} disableTypography={true}>
            {t('downloadOverallResults')}
          </ListItemText>
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={downloadZipFolder}>
          <ListItemIcon>
            <FolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText className={classes.menuText} disableTypography={true}>
            {t('downloadPersonaLists')}
          </ListItemText>
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={handleOpenResultsPreview(project.id)}>
          <ListItemIcon>
            <PreviewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText className={classes.menuText} disableTypography={true}>
            {t('preview')}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

const useStyles = makeStyles<{ isEven: boolean }>()((theme, { isEven }) => ({
  crawlerProject: {
    position: 'relative',
    width: '100%',
    padding: '8px',
    background: isEven ? '#FFF' : theme.palette.backgrounds.main,
    minHeight: '36px',
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    marginRight: '8px',
    color: theme.palette.greys.medium,
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(16),
      marginRight: '4px',
    },
  },
  projectName: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.greys.medium,
    lineHeight: 1,
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
    },
  },
  statusText: {
    fontSize: theme.typography.pxToRem(12),
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
    },
  },
  breadcrumbs: {
    '& ol': {
      flexWrap: 'wrap',
      gap: '8px',
    },
    '& .MuiBreadcrumbs-separator': {
      color: theme.palette.greys.medium,
      height: theme.typography.pxToRem(20),
      [theme.breakpoints.down(400)]: {
        height: theme.typography.pxToRem(16),
      },
    },
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  menuItem: {
    minHeight: '40px !important',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
    },
    [theme.breakpoints.down(400)]: {
      paddingLeft: '8px',
      paddingRight: '8px',
      minHeight: '24px !important',
      '& svg': {
        fontSize: theme.typography.pxToRem(16),
      },
    },
  },
  menuText: {
    fontSize: theme.typography.pxToRem(12),
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(10),
    },
  },
  warnIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.error.main,
    marginRight: '8px',
    [theme.breakpoints.down(400)]: {
      fontSize: theme.typography.pxToRem(16),
      marginRight: '4px',
    },
  },
}));

export default LinkedinProjectItem;
